import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import services from '../services';
import withMessage from './withMessage';
import withResponseHandling from './withResponseHandling';
import { acBackgroundArquivosChanged } from '../store/actions';

const PAGE_SIZE = 50;

const withArquivos = Component => {
  function WrapComponent(props) {
    const dispatch = useDispatch();

    const updatedAt = useSelector(state => state.background.arquivos.updatedAt);
    const entidadeId = useSelector(state => state.entidade.current.default_entidade_id);

    const [arquivos, setArquivos] = useState([]);
    const [includes, setIncludes] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [rowSelection, setRowSelection] = useState({});
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    });
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);

    const mapFilterValue = (pId, pValue) => {
      if (pId === 'origem') {
        const xMapping = {
          Manual: 'MAN',
          Bot: 'BOT'
        };

        return xMapping[pValue] || pValue;
      }

      return pValue;
    };

    const formatColumnFilters = useCallback((pColumnFilters = []) => {
      const xFilters = {};

      pColumnFilters.forEach(xItem => {
        if (xItem?.value === undefined || xItem?.value === null || xItem?.value === '') {
          return;
        }

        xFilters[xItem.id] = mapFilterValue(xItem.id, xItem.value);
      });

      return xFilters;
    }, []);

    const readArquivos = useCallback(
      (pParams = {}, pCallback) => {
        if (!arquivos.length) {
          setIsLoading(true);
        } else {
          setIsRefetching(true);
        }

        services.posicoes.arquivos(
          {
            params: {
              sort: 'data_envio desc',
              size: PAGE_SIZE,
              page: 1,
              available_filters: true,
              ...pParams
            }
          },
          rRes => {
            const xArquivos = Object.values(rRes.data || {});

            setArquivos(xArquivos);
            setIncludes(rRes.includes || {});
            setRowCount(rRes?.pages?.total_items || xArquivos.length);

            pCallback && pCallback(xArquivos);
          },
          rErr => {
            props.responseErrorHandling(rErr);
          },
          () => {
            setIsLoading(false);
            setIsRefetching(false);
          }
        );
      },
      [arquivos.length, props]
    );

    const deleteArquivos = (pFile, onFinish) => {
      const { data_envio, arquivo } = pFile;

      services.posicoes.uploadRemove(
        { data_envio, arquivo },
        () => {
          props.onMessageSuccess('Arquivo excluído com sucesso');
          readArquivos();
          setRowSelection({});
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          onFinish && onFinish();
        }
      );
    };

    const bulkDeleteArquivos = pRows => {
      const xArquivosId = pRows.map(xRow => {
        const { data_envio, arquivo, entidade_id } = xRow.original;

        return { data_envio, arquivo, entidade_id };
      });

      xArquivosId.forEach(xArquivo => {
        deleteArquivos(xArquivo);
      });
    };

    const handleFinishUpload = useCallback(() => {
      readArquivos();
      dispatch(acBackgroundArquivosChanged(true));
    }, [dispatch, readArquivos]);

    useEffect(() => {
      const xPage = pagination.pageIndex + 1;
      const xFilters = formatColumnFilters(columnFilters);

      readArquivos({
        page: xPage,
        size: pagination.pageSize,
        ...xFilters
      });
    }, [
      readArquivos,
      pagination.pageIndex,
      pagination.pageSize,
      updatedAt,
      entidadeId,
      sorting,
      columnFilters,
      formatColumnFilters
    ]);

    const xProps = {
      arquivos,
      includes,
      isLoading,
      isRefetching,
      rowCount,
      rowSelection,
      pagination,
      sorting,
      columnFilters,
      setPagination,
      setSorting,
      setColumnFilters,
      setRowSelection,
      readArquivos,
      deleteArquivos,
      bulkDeleteArquivos,
      handleFinishUpload
    };

    return <Component {...props} {...xProps} />;
  }

  return withMessage(withResponseHandling(WrapComponent));
};

export default withArquivos;
